import { ArrowRight, ArrowUpRight } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import type { MaterialItem } from "../../../api/library";
import { MediaImage } from "../../../components/MediaImage";
import { Skeleton } from "../../../components/Skeleton";
import { useScrollEnter } from "../../../hooks/useScrollEnter";
import { materialTabs, materialTint, materialTypeLabel, type MaterialTypeOption } from "../home-data";

type HomeMaterialsProps = {
  materials: MaterialItem[];
  types: MaterialTypeOption[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
};

export function HomeMaterials({ materials, types, loading, error, onRetry }: HomeMaterialsProps) {
  const [active, setActive] = useState("all");
  const sectionRef = useScrollEnter<HTMLElement>(0.45);
  const tabs = useMemo(() => materialTabs(materials, types), [materials, types]);
  const visible = useMemo(
    () => (active === "all" ? materials : materials.filter((item) => (item.type || "undefined") === active)),
    [active, materials],
  );

  return (
    <section ref={sectionRef} className="home-materials" id="materials" aria-labelledby="home-materials-title" aria-busy={loading}>
      <div className="home-materials-sheet">
        <header className="home-materials-head">
          <h2 id="home-materials-title">每一块材质样本，<br />随时带进你的场景</h2>
          <div>
            <p>每一块样本都带着颜色、纹理和表面工艺参数，在情绪板里一键赋给模型。</p>
            <Link className="home-text-link is-dark" to="/library?kind=material">打开材质库 <ArrowRight size={16} /></Link>
          </div>
        </header>
        {loading ? (
          <div className="home-material-grid" aria-hidden="true">
            {Array.from({ length: 16 }, (_, index) => (
              <span className="home-chip" key={index}><span className="home-chip-swatch"><Skeleton className="is-fill" /></span></span>
            ))}
          </div>
        ) : materials.length === 0 ? (
          <div className={error ? "home-status is-error" : "home-status"}>
            <p>{error ? "材质样本没有加载出来，请检查网络后重试。" : "材质库还在整理中，可以先去素材库看看模型。"}</p>
            {error ? <button type="button" onClick={onRetry}>重新加载</button> : <Link to="/library">打开素材库</Link>}
          </div>
        ) : (
          <>
            {tabs.length > 2 ? (
              <div className="home-material-tabs" role="tablist" aria-label="材质分类">
                {tabs.map((tab) => (
                  <button
                    key={tab.value}
                    type="button"
                    role="tab"
                    aria-selected={active === tab.value}
                    className={active === tab.value ? "is-active" : ""}
                    onClick={() => setActive(tab.value)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            ) : null}
            <div key={active} className="home-material-grid" aria-live="polite">
              {visible.slice(0, 15).map((item) => {
                const cover = item.coverThumbUrl || item.coverUrl || "";
                return (
                  <Link to="/library?kind=material" className="home-chip" key={item.id} aria-label={`在材质库查看${item.name}`}>
                    <span className="home-chip-swatch" style={{ backgroundColor: materialTint(item) }}>
                      {cover ? <MediaImage src={cover} alt={`${item.name}材质样本`} /> : null}
                    </span>
                    <span className="home-chip-copy">
                      <strong>{item.name}</strong>
                      <small>{item.code || materialTypeLabel(item.type, types)}</small>
                    </span>
                  </Link>
                );
              })}
              <Link className="home-chip is-all" to="/library?kind=material">
                <span className="home-chip-swatch"><ArrowUpRight size={28} strokeWidth={1.4} /></span>
                <span className="home-chip-copy">
                  <strong>{visible.length === 0 ? "这一类还在整理" : "查看全部材质"}</strong>
                  <small>{visible.length === 0 ? "去材质库看看其他分类" : "进入材质库"}</small>
                </span>
              </Link>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
